import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import logo from "../assets/antaryatra-logo-horizontal 3.0.svg";
import LogoutModal from "./LogoutModal";
import { Menu, X, UserRound, Compass, Bell } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import ThemeToggle from "./ThemeToggle";
import SanctuarySettings from "./SanctuarySettings";

/**
 * Product-ready Navbar:
 * - Responsive (desktop links + mobile hamburger)
 * - Accessible (aria attributes)
 * - Synchronous localStorage read (no useEffect)
 * - Uses serenity/semantic classes from tailwind config
 *
 * Replace existing file with this.
 */

function Avatar({ name, size = 8 }) {
  // simple initials avatar
  const initials = (name || "A")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className={`w-${size} h-${size} rounded-lg flex items-center justify-center bg-serenity-blue-500 text-white font-semibold`}
      style={{ width: `${size * 4}px`, height: `${size * 4}px` }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  // Hide navbar on auth/offboarding pages (you already had this behavior)
  const hideNavbar =
    location.pathname === "/login" ||
    location.pathname === "/onboarding" ||
    location.pathname === "/signup";
  if (hideNavbar) return null;

  // Robust check: Must have user data to render authenticated UI
  const isLoggedIn = !!user;

  /* ================= LOGOUT LOGIC ================= */

  // Trigger modal instead of direct logout
  const confirmLogout = () => {
    setShowLogoutModal(true);
    setOpen(false); // Close mobile menu if open
  };

  const finalizeLogout = async () => {
    await logout();
    navigate("/login");
    setShowLogoutModal(false);
  };

  const links = [
    { to: "/dashboard", label: t('nav_space') },
    { to: "/chat", label: t('nav_chats') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={finalizeLogout}
      />

      <header className="border-b border-gray-100 dark:border-gray-800 bg-spiritual-white dark:bg-spiritual-dark transition-colors duration-200">
        <div className="max-w-container-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo */}
            <div className="flex items-center gap-3 overflow-hidden py-1">
              <Link
                to={isLoggedIn ? "/dashboard" : "/"}
                className="flex items-center gap-2 group no-underline"
              >
                {/* Primary Site Logo */}
                <img
                  src={logo}
                  alt="Antaryatra"
                  className="h-16 w-auto object-contain"
                />
              </Link>


            </div>

            {/* Center: Desktop Links (Visible to all, but restricted if guest) */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Primary navigation">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="relative"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive(link.to)
                      ? "text-brand-blue bg-blue-50 dark:bg-brand-blue/10"
                      : "text-gray-600 dark:text-gray-300 hover:text-brand-blue hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                  >
                    {link.label}
                  </motion.div>
                  {isActive(link.to) && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute inset-0 rounded-full border-2 border-brand-blue/20 pointer-events-none"
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* Right: actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              {isLoggedIn && (
                <Link to="/notifications" className="relative group hidden sm:block">
                  <motion.div
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2.5 rounded-xl bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm border border-gray-100 dark:border-gray-700 transition-all flex items-center justify-center relative"
                  >
                    <Bell className="w-5 h-5 text-gray-500 group-hover:text-brand-blue transition-colors" />
                    <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-gray-900 shadow-sm" />
                  </motion.div>
                </Link>
              )}

              <ThemeToggle />

              {isLoggedIn ? (
                <>
                  <div
                    onClick={() => navigate("/profile")}
                    className="hidden md:flex items-center gap-2 px-1 py-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    role="button"
                  >
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 hidden md:block px-2">
                        {user.name}
                      </div>
                      <div className="w-9 h-9 rounded-full bg-brand-gradient p-[2px]">
                        <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                          {user.avatar ? (
                            <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" />
                          ) : (
                            <UserRound className="w-5 h-5 text-brand-blue" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={confirmLogout}
                    className="hidden sm:block px-4 py-2 rounded-full text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/10 dark:hover:bg-red-900/20 transition-colors"
                  >
                    {t('nav_logout')}
                  </motion.button>
                </>
              ) : (
                <div className="flex items-center gap-1 sm:gap-3">
                  <Link to="/login">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-full text-gray-600 dark:text-gray-300 text-[10px] sm:text-sm font-medium hover:text-brand-blue transition"
                    >
                      {t('nav_signin')}
                    </motion.button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <SanctuarySettings 
          isOpen={showSettings} 
          onClose={() => setShowSettings(false)} 
        />
      </header>
    </>
  );
}
